import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
  useFormContext,
} from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { useEffect, useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormControl } from "@/components/ui/form";
import { useGetFacilitiesQuery } from "@/services/facilityApis";
import Combobox from "@/components/ui/common/combobox";
import { useGetIncomeCategoryQuery } from "@/services/incomeCategoryApi";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { EventBookingType } from "@/types/enums";
import { Switch } from "@/components/ui/switch";
import "react-international-phone/style.css";
import { PhoneNumberUtil } from "google-libphonenumber";
import { FacilitySchedule } from "@/types/facility/schedule";
import { useToast } from "@/components/ui/use-toast";
import { useGetTaxGroupQuery } from "@/services/salesTaxApi";

interface EventGeneralInfoFormProps {
  control: Control<FacilitySchedule>;
  errors: FieldErrors<FacilitySchedule>;
  watch: UseFormWatch<FacilitySchedule>;
  setValue: UseFormSetValue<FacilitySchedule>;
  trigger?: UseFormTrigger<FacilitySchedule>;
  action: 'add' | 'edit';
  open: boolean;
}

const colorOptions = [
  // Row 1
  { value: "red-500", label: "Red", class: "bg-red-500" },
  { value: "orange-500", label: "Orange", class: "bg-orange-500" },
  { value: "yellow-500", label: "Yellow", class: "bg-yellow-500" },
  { value: "green-500", label: "Green", class: "bg-green-500" },
  { value: "teal-500", label: "Teal", class: "bg-teal-500" },
  { value: "blue-500", label: "Blue", class: "bg-blue-500" },
  { value: "purple-500", label: "Purple", class: "bg-purple-500" },
  // Row 2
  { value: "red-600", label: "Dark Red", class: "bg-red-600" },
  { value: "orange-600", label: "Dark Orange", class: "bg-orange-600" },
  { value: "yellow-600", label: "Dark Yellow", class: "bg-yellow-600" },
  { value: "green-600", label: "Dark Green", class: "bg-green-600" },
  { value: "teal-600", label: "Dark Teal", class: "bg-teal-600" },
  { value: "blue-600", label: "Dark Blue", class: "bg-blue-600" },
  { value: "purple-600", label: "Dark Purple", class: "bg-purple-600" },
  // Row 3
  { value: "red-700", label: "Darker Red", class: "bg-red-700" },
  { value: "orange-700", label: "Darker Orange", class: "bg-orange-700" },
  { value: "yellow-700", label: "Darker Yellow", class: "bg-yellow-700" },
  { value: "green-700", label: "Darker Green", class: "bg-green-700" },
  { value: "teal-700", label: "Darker Teal", class: "bg-teal-700" },
  { value: "blue-700", label: "Darker Blue", class: "bg-blue-700" },
  { value: "purple-700", label: "Darker Purple", class: "bg-purple-700" },
];

const EventGeneralInfoForm = ({
  control,
  errors,
  watch,
  setValue,
  action,
  open,
}: EventGeneralInfoFormProps) => {
  // Use form context for better integration
  const {
    control: contextControl,
    formState: { errors: contextErrors },
    watch: contextWatch,
    setValue: contextSetValue,
    trigger: contextTrigger,
  } = useFormContext<FacilitySchedule>();

  // Use context versions if available, otherwise fall back to props
  const formControl = contextControl || control;
  const formWatch = contextWatch || watch;
  const bookingTypeValue = formWatch("general_info.is_exclusive");

  const { toast } = useToast();

  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const { data: facilityAsAddOns } = useGetFacilitiesQuery({
    query: "status=active&bookable=true&load_add_ons=true&is_add_on=false&sort_key=id&sort_order=desc",
    orgId: orgId,
  });

  const { data: incomeCatData } = useGetIncomeCategoryQuery(
    { org_id: orgId, query: "sort_key=id&sort_order=desc&status=active" },
    {
      skip: !orgId && !open,
    });


  const { data: taxGroupData } = useGetTaxGroupQuery({ query: "", org_id: orgId }, {
    skip: !orgId && !open,
  });


  const incomeCatOptions = useMemo(() => {
    return Array.isArray(incomeCatData?.data)
      ? incomeCatData?.data.map((incomeCat) => ({
        ...incomeCat,
        label: incomeCat?.name,
        value: incomeCat?.id?.toString() || "",
      }))
      : [];
  }, [incomeCatData]);

  const taxGroupOptions = useMemo(() => {
    return Array.isArray(taxGroupData?.data)
      ? taxGroupData?.data.map((taxGroup) => ({
        ...taxGroup,
        label: taxGroup?.name,
        value: taxGroup?.id?.toString() || "",
      }))
      : [];
  }, [taxGroupData]);


  const facilityOptions = useMemo(() => {
    return Array.isArray(facilityAsAddOns?.data)
      ? facilityAsAddOns?.data.map((facility) => ({
        ...facility,
        label: facility?.name || "",
        value: facility?.id?.toString() || "",
      }))
      : [];
  }, [facilityAsAddOns]);

  // Get selected facility's max capacity for validation
  const selectedFacilityCapacity = useMemo(() => {
    const selectedFacilityId = formWatch("general_info.facility_id");
    if (selectedFacilityId && facilityAsAddOns?.data) {
      const facility = facilityAsAddOns.data.find(
        (f) => f.id === selectedFacilityId
      );
      return facility?.max_capacity || null;
    }
    return null;
  }, [formWatch("general_info.facility_id"), facilityAsAddOns]);

  // Get selected facility's availability
  const selectedFacilityAvailability = useMemo(() => {
    const selectedFacilityId = formWatch("general_info.facility_id");
    if (selectedFacilityId && facilityAsAddOns?.data) {
      const facility = facilityAsAddOns.data.find(
        (f) => f.id === selectedFacilityId
      );
      return facility?.availability || null;
    }
    return null;
  }, [formWatch("general_info.facility_id"), facilityAsAddOns]);

  // Type for day names matching Availability keys
  type DayName = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

  // Helper function to get day name from date
  const getDayName = (date: Date): DayName => {
    const dayIndex = date.getDay();
    const dayMap: Record<number, DayName> = {
      0: 'sunday',
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'saturday',
    } as const;
    return dayMap[dayIndex] || 'sunday'; // Default fallback
  };

  // Function to check if all dates in range fall on unavailable days
  const checkDatesAgainstFacilityAvailability = (
    startDateStr: string | undefined,
    endDateStr: string | undefined
  ): boolean => {
    if (!startDateStr || !endDateStr || !selectedFacilityAvailability) {
      return true; // Skip validation if dates or availability not available
    }

    try {
      const startDate = new Date(startDateStr);
      const endDate = new Date(endDateStr);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return true; // Skip validation for invalid dates
      }

      // Normalize dates
      const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

      // Get all unique days in the date range
      const daysInRange = new Set<DayName>();
      const currentDate = new Date(start);

      while (currentDate <= end) {
        const dayName = getDayName(currentDate);
        daysInRange.add(dayName);
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Check if ALL days in the range are unavailable
      let allDaysUnavailable = true;
      for (const dayName of daysInRange) {
        // Type assertion is safe here because dayName is guaranteed to be a valid key
        const dayAvailability = selectedFacilityAvailability[dayName as keyof typeof selectedFacilityAvailability];
        if (dayAvailability?.is_available) {
          allDaysUnavailable = false;
          break;
        }
      }

      return !allDaysUnavailable; // Return true if validation passes (at least one day is available)
    } catch (error) {
      console.error("Error checking facility availability:", error);
      return true; // Skip validation on error
    }
  };


  return (
    <div className="space-y-4">

      <Card className="p-4">
        <p className="font-medium text-sm">Event Details</p>
        <Separator className="h-[1px]" />

        {/* 1st row */}
        <div className="grid grid-cols-4 items-start gap-4 mt-4">
          <div>
            <Controller
              name="general_info.name"
              rules={{
                required: "Required",
                maxLength: {
                  value: 100,
                  message: "Event Name should not exceed 100 characters",
                },
                validate: (value) => {
                  if (value && value.trim().length === 0) {
                    return "Required";
                  } else if (!/^[a-zA-Z0-9\s']+$/.test(value || "")) {
                    return "Event Name should only contain letters, numbers and spaces";
                  }

                  return true;
                },
              }}
              control={formControl}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm">
                    Event Name <span className="text-red-500">*</span>
                  </p>
                  <Input
                    type="text"
                    id="name"
                    placeholder="Enter Event Name"
                    value={value || ""}
                    onChange={onChange}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          <div>
            <Controller
              name="general_info.facility_id"
              control={formControl}
              rules={{
                validate: (value) =>
                  (value !== undefined && value !== null && value !== 0) ||
                  "Required",
              }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm">
                    Facility <span className="text-red-500">*</span>
                  </p>
                  <Combobox
                    list={facilityOptions}
                    defaultValue={
                      facilityOptions?.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = facilityOptions?.find(
                        (option) => option.label === selectedLabel
                      );
                      onChange(
                        selectedOption
                          ? Number(selectedOption.value)
                          : undefined
                      );
                    }}
                    label="Facility"
                    showLabel={false}
                    disabled={action === "edit"}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          <div>
            <Controller
              name="general_info.trainer_name"
              rules={{
                maxLength: {
                  value: 100,
                  message: "Trainer Name should not exceed 100 characters",
                },
              }}
              control={formControl}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm">
                    Trainer Name
                  </p>
                  <Input
                    type="text"
                    id="name"
                    placeholder="Enter Trainer Name"
                    value={value || ""}
                    onChange={onChange}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          <div>
            <Controller
              name="general_info.income_category_id"
              rules={{
                validate: (value) =>
                  (value !== undefined && value !== null && value !== 0) ||
                  "Required",
              }}
              control={formControl}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm">
                    Income Category <span className="text-red-500">*</span>
                  </p>
                  <Combobox
                    list={incomeCatOptions}
                    defaultValue={
                      incomeCatOptions?.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = incomeCatOptions?.find(
                        (option) => option.label === selectedLabel
                      );
                      onChange(
                        selectedOption
                          ? Number(selectedOption.value)
                          : undefined
                      );
                    }}
                    label="Income Category"
                    showLabel={false}
                    disabled={action === "edit"}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>
          <div>
            <Controller
              name="general_info.tax_group_id"
              rules={{
                validate: (value) =>
                  (value !== undefined && value !== null && value !== 0) ||
                  "Required",
              }}
              control={formControl}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm">
                    Tax Group <span className="text-red-500">*</span>
                  </p>
                  <Combobox
                    list={taxGroupOptions}
                    defaultValue={
                      taxGroupOptions?.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = taxGroupOptions?.find(
                        (option) => option.label === selectedLabel
                      );
                      onChange(
                        selectedOption
                          ? Number(selectedOption.value)
                          : undefined
                      );
                    }}
                    label="Tax Group"
                    showLabel={false}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          <div>
            <p className="text-sm">
              Event Capacity <span className="text-red-500">*</span>
            </p>
            <Controller
              control={formControl}
              name="general_info.max_attendees"
              rules={{
                required: "Required",
                min: {
                  value: 1,
                  message: "Minimum attendees is 1",
                },
                max: {
                  value: 5000,
                  message: "Max Attendees cannot be greater than 5000",
                },
                validate: (value) => {
                  if (
                    !formWatch("general_info.is_exclusive") &&
                    selectedFacilityCapacity &&
                    value &&
                    value > selectedFacilityCapacity
                  ) {
                    return `Max Attendees cannot exceed the facility capacity of ${selectedFacilityCapacity}.`;
                  } else if (value == selectedFacilityCapacity) {
                    setValue("general_info.is_exclusive", true);
                    contextTrigger("general_info.is_exclusive");
                  }
                  return true;
                },
              }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <Input
                    type="number"
                    id="max_attendees"
                    step="1"
                    min={1}
                    placeholder="Enter People"
                    value={value || ""}
                    onChange={(e) => onChange(Number(e.target.value))}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>


          <div className="flex flex-col justify-end">
            <span className=" p-0 text-sm text-black left-2 -top-1.5 px-1 bg-white">
              Start Date{""}
              <span className="text-red-500">*</span>
            </span>
            <Controller
              name="general_info.start_date"
              rules={{
                required: "Required",
                validate: (value) => {
                  if (!value) return true; // Let required handle empty
                  const endDate = formWatch("general_info.end_date");
                  if (endDate) {
                    const start = new Date(value);
                    const end = new Date(endDate);
                    if (isNaN(start.getTime()) || isNaN(end.getTime())) return true; // Invalid dates handled elsewhere
                    // Normalize dates to compare only date part (set time to midnight)
                    const startNormalized = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                    const endNormalized = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                    if (startNormalized > endNormalized) {
                      return "Start date cannot be greater than end date";
                    }

                    // Check facility availability
                    if (!checkDatesAgainstFacilityAvailability(value, endDate)) {
                      return "Event cannot be scheduled on unavailable facility days";
                    }
                  }
                  return true;
                }
              }}
              control={formControl}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { invalid, error },
              }) => {
                let dateValue: Date | undefined;
                try {
                  dateValue = value ? new Date(value as unknown as string) : undefined;
                  if (dateValue && isNaN(dateValue.getTime())) {
                    dateValue = undefined;
                  }
                } catch {
                  dateValue = undefined;
                }

                return (
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <div className="relative">

                          <Button
                            variant={"outline"}
                            type="button"
                            className={cn(
                              "w-full pl-3 text-gray-800 text-left font-normal hover:bg-transparent border-[1px]"
                            )}
                          >
                            {dateValue ? (
                              format(dateValue, "dd-MM-yyyy")
                            ) : (
                              <span>Select start date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                          {error && (
                            <span className="text-red-500 text-xs mt-[5px]">
                              {error.message}
                            </span>
                          )}
                        </div>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="center">
                      <Calendar
                        mode="single"
                        captionLayout="dropdown-buttons"
                        selected={dateValue}
                        defaultMonth={dateValue || new Date()}
                        onSelect={(selectedDate) => {
                          if (selectedDate) {
                            const formattedDate = format(selectedDate, "yyyy-MM-dd");
                            onChange(formattedDate);

                            // Check facility availability after date selection
                            const endDate = formWatch("general_info.end_date");
                            if (endDate && !checkDatesAgainstFacilityAvailability(formattedDate, endDate)) {
                              setTimeout(() => {
                                toast({
                                  variant: "destructive",
                                  title: "Invalid Date Range",
                                  description: "Event cannot be scheduled on unavailable facility days",
                                });
                              }, 0);
                            }
                          }
                        }}
                        disabled={(date) =>
                          date.getTime() < new Date().setHours(0, 0, 0, 0)
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                );
              }}
            />
          </div>

          <div className="flex flex-col justify-end">
            <span className="p-0 text-sm text-black left-2 -top-1.5 px-1 bg-white">
              End Date{""}
              <span className="text-red-500">*</span>
            </span>
            <Controller
              name="general_info.end_date"
              rules={{
                required: "Required",
                validate: (value) => {
                  if (!value) return true; // Let required handle empty
                  const startDate = formWatch("general_info.start_date");
                  if (startDate) {
                    const start = new Date(startDate);
                    const end = new Date(value);
                    if (isNaN(start.getTime()) || isNaN(end.getTime())) return true; // Invalid dates handled elsewhere
                    // Normalize dates to compare only date part (set time to midnight)
                    const startNormalized = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                    const endNormalized = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                    if (startNormalized > endNormalized) {
                      return "End date cannot be less than start date";
                    }

                    // Check facility availability
                    if (!checkDatesAgainstFacilityAvailability(startDate, value)) {
                      return "Event cannot be scheduled on unavailable facility days";
                    }
                  }
                  return true;
                }
              }}
              control={formControl}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { invalid, error },
              }) => {
                let dateValue: Date | undefined;
                let defaultMonth: Date = new Date();

                try {
                  dateValue = value ? new Date(value as unknown as string) : undefined;
                  if (dateValue && isNaN(dateValue.getTime())) {
                    dateValue = undefined;
                  }

                  const startDateStr = formWatch("general_info.start_date");
                  if (startDateStr) {
                    const startDate = new Date(startDateStr);
                    if (!isNaN(startDate.getTime())) {
                      defaultMonth = startDate;
                    }
                  }
                } catch {
                  dateValue = undefined;
                }

                return (
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <div className="relative">
                          <Button
                            variant={"outline"}
                            type="button"
                            className={cn(
                              "w-full pl-3 text-gray-800 text-left font-normal hover:bg-transparent border-[1px]"
                            )}
                          >
                            {dateValue ? (
                              format(dateValue, "dd-MM-yyyy")
                            ) : (
                              <span>Select End date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                          {error && (
                            <span className="text-red-500 text-xs mt-[5px]">
                              {error.message}
                            </span>
                          )}
                        </div>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="center">
                      <Calendar
                        mode="single"
                        captionLayout="dropdown-buttons"
                        selected={dateValue}
                        defaultMonth={defaultMonth}
                        onSelect={(selectedDate) => {
                          if (selectedDate) {
                            const formattedDate = format(selectedDate, "yyyy-MM-dd");
                            onChange(formattedDate);

                            // Check facility availability after date selection
                            const startDate = formWatch("general_info.start_date");
                            if (startDate && !checkDatesAgainstFacilityAvailability(startDate, formattedDate)) {
                              setTimeout(() => {
                                toast({
                                  variant: "destructive",
                                  title: "Invalid Date Range",
                                  description: "Event cannot be scheduled on unavailable facility days",
                                });
                              }, 0);
                            }
                          }
                        }}
                        disabled={(date) => {
                          try {
                            const startDateStr = formWatch("general_info.start_date");
                            if (startDateStr) {
                              const startDate = new Date(startDateStr);
                              if (!isNaN(startDate.getTime())) {
                                // Normalize dates to compare only date part (set time to midnight)
                                const startNormalized = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                                const dateNormalized = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                                const today = new Date(new Date().setHours(0, 0, 0, 0));
                                // Allow same date as start date, but not dates before start date or today
                                return dateNormalized < startNormalized || dateNormalized < today;
                              }
                            }
                            const today = new Date(new Date().setHours(0, 0, 0, 0));
                            const dateNormalized = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                            return dateNormalized < today;
                          } catch {
                            const today = new Date(new Date().setHours(0, 0, 0, 0));
                            const dateNormalized = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                            return dateNormalized < today;
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                );
              }}
            />
          </div>

          <div>
            <p className="text-sm font-normal">Location</p>
            <Controller
              control={formControl}
              name="general_info.location"
              rules={{
                maxLength: {
                  value: 200,
                  message: "Location cannot exceed 200 characters",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Textarea
                    placeholder="Enter Location"
                    className="resize-none"
                    {...field}
                    rows={4}
                    value={field.value ?? ""}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          <div>
            <p className="text-sm font-normal">Description</p>
            <Controller
              control={formControl}
              name="general_info.description"
              rules={{
                maxLength: {
                  value: 1000,
                  message: "Description cannot exceed 1000 characters",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Textarea
                    placeholder="Type your Description here"
                    className="resize-none"
                    {...field}
                    rows={4}
                    value={field.value ?? ""}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>


          <div>
            <Controller
              control={formControl}
              name="general_info.color"
              rules={{ required: "Required" }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <div>
                  <p className="text-sm">
                    Colour <span className="text-red-500">*</span>
                  </p>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between"
                      >
                        <div className="flex items-center">
                          {value && (
                            <div
                              className={`w-4 h-4 rounded-full mr-2 ${colorOptions.find((c) => c.value === value)
                                ?.class
                                }`}
                            ></div>
                          )}
                          <span className="font-normal text-sm">
                            {value
                              ? colorOptions.find((c) => c.value === value)
                                ?.label
                              : "Select Colour"}
                          </span>
                        </div>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-3" align="start">
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">Set Colour</h4>
                        <div className="grid grid-cols-7 gap-2">
                          {colorOptions.map((color, index) => (
                            <button
                              key={index}
                              type="button"
                              className={cn(
                                "w-6 h-6 rounded-full",
                                color.class,
                                value === color.value &&
                                "ring-2 ring-offset-2 ring-black"
                              )}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onChange(color.value);
                              }}
                              title={color.label}
                            />
                          ))}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </div>
              )}
            />
          </div>


          {/* <div>
            <Controller
              control={formControl}
              name="general_info.is_exclusive"
              render={({ field: { onChange, value } }) => (
                <>
                  <p className="text-sm">Booking Type <span className="text-red-500">*</span></p>
                  <div className="flex rounded-md overflow-hidden">
                    <button
                      type="button"
                      onClick={() => onChange(true)}
                      className={cn(
                        "py-1 px-2  h-10 text-xs w-full",
                        value === true ? "bg-primary text-white" : "bg-gray-100 text-gray-700"
                      )}
                    >
                      Fully
                    </button>
                    <button
                      type="button"
                      onClick={() => onChange(false)}  
                      className={cn(
                        "py-1 px-2  h-10 text-xs w-full",
                        value === false ? "bg-primary text-white" : "bg-gray-100 text-gray-700"
                      )}
                    >
                      Partial
                    </button>
                  </div>
                </>
              )}
            />
          </div> */}
        </div>
      </Card >


    </div >
  );
};

export default EventGeneralInfoForm;
